import { useEffect, useState } from "react";
import { Bookmark, Layers, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

export const PolishPresets = ({ book, version, model, selected, onPick }) => {
  const [list, setList] = useState([]);
  const [series, setSeries] = useState([]);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const sid = book.series_id;
  useEffect(() => { api.get(`/books/${book.id}/polish-presets`).then((r) => setList(r.data)).catch(() => {}); }, [book.id]);
  useEffect(() => { if (sid) api.get(`/series/${sid}/polish-presets`).then((r) => setSeries(r.data)).catch(() => {}); }, [sid]);
  const all = selected.length === version.chapters.length;
  const save = async () => {
    if (!name.trim()) return toast.error("Give the preset a name");
    try {
      if (shared) setSeries((await api.post(`/series/${sid}/polish-presets`, { name: name.trim(), model })).data);
      else setList((await api.post(`/books/${book.id}/polish-presets`, { name: name.trim(), model, chapter_ids: all ? [] : selected })).data);
      setName(""); setSaving(false); setShared(false);
      toast.success(shared ? `Preset shared with every book in ${book.series_name}` : "Preset saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (p) => {
    try {
      if (p.series) setSeries((await api.delete(`/series/${sid}/polish-presets/${p.id}`)).data);
      else setList((await api.delete(`/books/${book.id}/polish-presets/${p.id}`)).data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pick = (p) => {
    const ids = version.chapters.map((c) => c.id);
    const keep = p.chapter_ids.filter((id) => ids.includes(id));
    if (p.chapter_ids.length && keep.length < p.chapter_ids.length) toast.message(keep.length ? "Some saved chapters no longer exist and were skipped" : "Saved chapters no longer exist — whole book ticked");
    onPick({ model: p.model, selected: keep.length ? keep : ids });
    toast.success(`"${p.name}" filled in — press Start when ready`);
  };
  const label = (p) => (p.series ? "series · whole book" : p.chapter_ids.length ? `${p.chapter_ids.length} ch.` : "whole book");
  const chips = [...series, ...list];
  return (
    <div className="space-y-2" data-testid="polish-presets">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Bookmark size={12} />Presets</span>
        {chips.length === 0 && <span className="text-xs text-slate-500" data-testid="polish-presets-empty">None yet</span>}
        {chips.map((p) => (
          <span key={p.id} className={`inline-flex items-center rounded-full border transition-colors hover:border-amber-500/60 ${p.series ? "border-emerald-600/50" : "border-slate-700"}`} data-testid={`polish-preset-${p.id}`}>
            <button type="button" onClick={() => pick(p)} className="pl-3 pr-1 py-1 text-xs text-slate-200 inline-flex items-center gap-1" data-testid={`polish-preset-use-${p.id}`}>{p.series && <Layers size={11} className="text-emerald-300" />}{p.name} <span className="text-slate-500">· {label(p)}</span></button>
            <button type="button" onClick={() => remove(p)} aria-label={`Delete ${p.name}`} className="pr-2 pl-1 py-1 text-slate-500 hover:text-rose-300" data-testid={`polish-preset-delete-${p.id}`}><X size={12} /></button>
          </span>
        ))}
        {!saving && <button type="button" onClick={() => setSaving(true)} className="text-xs text-amber-300 hover:underline inline-flex items-center gap-1" data-testid="polish-preset-new"><Save size={12} />Save current settings</button>}
      </div>
      {saving && (
        <div className="space-y-2" data-testid="polish-preset-form">
          <div className="flex flex-wrap items-center gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Preset name, e.g. Opening chapters" onKeyDown={(e) => e.key === "Enter" && save()}
              className="h-8 flex-1 min-w-[180px] rounded-md bg-slate-900 border border-slate-700 px-2 text-sm" data-testid="polish-preset-name" />
            <button type="button" onClick={save} className="h-8 px-3 rounded-md text-xs bg-emerald-700 hover:bg-emerald-600 text-white" data-testid="polish-preset-save">Save</button>
            <button type="button" onClick={() => { setSaving(false); setName(""); setShared(false); }} className="h-8 px-2 text-xs text-slate-400 hover:text-slate-200" data-testid="polish-preset-cancel">Cancel</button>
          </div>
          {sid && <label className="text-xs text-slate-400 flex gap-2 items-center"><input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} data-testid="polish-preset-series-toggle" />Share with every book in {book.series_name} (remembers the model; polishes the whole book)</label>}
        </div>
      )}
    </div>
  );
};
